/**
 * Parse a grounded answer's small Markdown subset into blocks, so Explore can
 * render paragraphs, bullet / numbered lists, sub-headings and **bold** —
 * without an HTML-producing Markdown library (model output is never rendered
 * as HTML, so a passage that smuggles markup into an answer stays inert).
 *
 * Citation markers (`[1]`) become `cite` tokens. The backend has already
 * validated and renumbered them; a number with no matching citation is kept as
 * plain text rather than dropped, so the answer is never silently edited.
 * Pure (no React) so it is unit-tested directly.
 */

export type Inline =
  | { kind: "text"; text: string }
  | { kind: "bold"; text: string }
  | { kind: "code"; text: string }
  | { kind: "cite"; n: number };

export interface ListItem {
  depth: number;
  inline: Inline[];
}

export type Block =
  | { kind: "heading"; inline: Inline[] }
  | { kind: "paragraph"; inline: Inline[] }
  | { kind: "list"; ordered: boolean; items: ListItem[] }
  | { kind: "table"; header: Inline[][]; rows: Inline[][][] };

const HEADING = /^\s{0,3}#{1,6}\s+(.*)$/;
const BULLET = /^(\s*)[-*•]\s+(.*)$/;
const ORDERED = /^(\s*)\d+[.)]\s+(.*)$/;
const INLINE = /\*\*([^*]+)\*\*|\[(\d+)\]|`([^`]+)`/g;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_RULE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function cells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

export function parseInline(text: string, known: ReadonlySet<number>): Inline[] {
  const out: Inline[] = [];
  const pushText = (value: string) => {
    if (!value) return;
    const prev = out.at(-1);
    if (prev?.kind === "text") prev.text += value;
    else out.push({ kind: "text", text: value });
  };
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    const index = match.index ?? 0;
    pushText(text.slice(last, index));
    if (match[1] !== undefined) {
      out.push({ kind: "bold", text: match[1] });
    } else if (match[3] !== undefined) {
      out.push({ kind: "code", text: match[3] });
    } else {
      const n = Number(match[2]);
      if (known.has(n)) out.push({ kind: "cite", n });
      else pushText(match[0]);
    }
    last = index + match[0].length;
  }
  pushText(text.slice(last));
  return out;
}

export function parseAnswer(text: string, known: ReadonlySet<number>): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: { depth: number; text: string }[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ kind: "paragraph", inline: parseInline(paragraph.join(" "), known) });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push({
        kind: "list",
        ordered: list.ordered,
        items: list.items.map((i) => ({ depth: i.depth, inline: parseInline(i.text, known) })),
      });
      list = null;
    }
  };

  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = (lines[i] ?? "").trimEnd();
    // A Markdown table: a `| a | b |` header, a `|---|---|` rule, then rows.
    if (TABLE_ROW.test(line) && TABLE_RULE.test(lines[i + 1] ?? "")) {
      flushParagraph();
      flushList();
      const header = cells(line).map((c) => parseInline(c, known));
      const rows: Inline[][][] = [];
      i += 2;
      while (i < lines.length && TABLE_ROW.test(lines[i] ?? "")) {
        rows.push(cells(lines[i] ?? "").map((c) => parseInline(c, known)));
        i++;
      }
      i--;
      blocks.push({ kind: "table", header, rows });
      continue;
    }
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ kind: "heading", inline: parseInline(heading[1] ?? "", known) });
      continue;
    }
    const item = BULLET.exec(line) ?? ORDERED.exec(line);
    if (item) {
      flushParagraph();
      const ordered = !BULLET.test(line);
      const depth = (item[1] ?? "").length >= 2 ? 1 : 0;
      if (!list || (depth === 0 && list.ordered !== ordered)) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push({ depth, text: item[2] ?? "" });
      continue;
    }
    if (list) {
      // A wrapped continuation of the previous list item.
      const lastItem = list.items.at(-1);
      if (lastItem) lastItem.text += ` ${line.trim()}`;
      continue;
    }
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushList();
  return blocks;
}
